// Photograph the sheet (LB-363, child of LB-364 Homework practice).
//
// Everything runs ON THE DEVICE. Tesseract.js reads the photo in a web worker; the language
// model and the reader's code are self-hosted under /vendor/, loaded the first time a parent
// taps Photograph and kept by the browser afterwards; the photo never leaves the phone, so the
// Privacy Policy needs no change. Nothing here is in the service worker's precache, so a
// household that never photographs a sheet never downloads any of it.
//
// The accuracy trial (LB-363, 22 Sep, a real shadowed and angled sheet): the untouched photo
// lost 6 of 20 rows; shadow removal + adaptive threshold + page-segmentation mode 4 read 19 of
// 20, and a dictionary pass caught the one miss and suggested the right word. So:
//   1. preprocessImage():  shadow removal (divide by the local background) then an adaptive
//      threshold — the trial's clean-up, on ImageData, integral-image fast
//   2. readImage():        Tesseract, psm 4 (one column of text, variable sizes)
//   3. parseSheetLines():  numbering and headings dropped, first word after "1)" for a
//      spelling list (the rest of a row is the write-then-cover columns), pairs for vocab
//   4. checkWords():       flag a word the dictionary does not know and OFFER a suggestion.
//      A suggestion is never applied for the parent: a misread word that slipped through would
//      become a child practising the wrong spelling.
//
// THE REAL-SHEET ROUND (LB-363, 25 Sep). Six actual sheets out of two school bags — two ruled
// spelling lists, three bordered Latin tables, a look-cover-write-check sheet. Measured against
// the code above, it lost most of the words on all of them: 9 of 20, 12 of 20, 4 of 15, 0 of 11.
// The clean-up was not at fault — the image it hands over is crisp. Every word sits BETWEEN TWO
// RULED LINES, and Tesseract's layout analysis takes the rules for text and throws the row away.
// Erasing the rules before the read, with nothing else changed, turned those into 20/20, 19/20,
// 15/15 and 11/11. So the clean-up now has two more steps and the parse has a second path:
//   1b. the rules are FOUND (their positions are the table's own geometry) and ERASED
//   1c. speckle is dropped — without it Tesseract returns one 1000px-wide box per row at
//       confidence 42 instead of the words, and the geometry below has nothing to place
//   3b. parseSheetCells(): when a sheet has rules, rows come from the BANDS BETWEEN THEM and
//       columns from the vertical rules, not from Tesseract's own text lines. That is what
//       reads a cell whose text wraps onto a second line ("You (sg.)" over "love") as one
//       answer. A sheet with no rules — a plain typed list — still goes through 3.
// Two things that bit and are now asserted in scripts/check-ocr.mjs: a band needs the image
// EDGES as boundaries or the first and last row of a table fall outside every band; and a word
// Tesseract scored 0 may still be a perfectly good word ("amamus", read exactly right, scored 0
// for a speck it took to be an apostrophe), so confidence alone must not discard one.
//
// The pure parts (1, 3, 4 and the helpers) take plain objects so scripts/check-ocr.mjs can run
// them in Node against synthetic sheets; only loadReader()/readImage() touch the browser.

import { normaliseAnswer, parseHomeworkText } from "./homework.js";
import { containsRudeWord } from "./rude-words.js";

// Versioned directories on purpose: the service worker serves same-origin files it has seen
// cache-first, so a new tesseract.js or model must land under a NEW path or a phone keeps the
// old one for good. Bump the directory when you bump the package.
export const OCR_VENDOR = {
  script: "/vendor/tesseract/7.0.0/tesseract.min.js",
  worker: "/vendor/tesseract/7.0.0/worker.min.js",
  coreSimd: "/vendor/tesseract/7.0.0/tesseract-core-simd-lstm.wasm.js",
  coreBasic: "/vendor/tesseract/7.0.0/tesseract-core-lstm.wasm.js",
  langPath: "/vendor/tessdata/eng-fast-4.1", // eng.traineddata.gz, tessdata_fast (md5 of the .traineddata d1be414f…)
  dictionary: "/vendor/dict/en-gb.txt", // SCOWL via Debian wbritish, see LICENSE-SCOWL.txt
};
export const OCR_MAX_WIDTH = 1600; // the crop is read at this width at most: enough for print, quick on a phone
export const OCR_PSM = "4";
// What the reader may return (Alan, 4 Oct: "We know that the words it will be reading are from
// the alphabet of Latin languages only"). Letters A-Z with the accented and macron letters of the
// languages a list can be in, digits for a sheet's numbering, and the marks a list uses. Not "|",
// "_" or quotes: the table's ruled line came back as "|", a write-in underline as "___", and both
// ended up in answers. The SPACE must be in it: without it Tesseract's line reader stops
// spacing words ("Week3spellings"), which halved the real Latin sheets' scores when measured.
// It cannot stop a real letter standing in for another (I read as l): that is fixLonePronoun's
// and the word check's job.
export const OCR_CHARS = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz"
  + "ÀÁÂÃÄÅÆÇÈÉÊËÌÍÎÏÑÒÓÔÕÖØÙÚÛÜÝàáâãäåæçèéêëìíîïñòóôõöøùúûüýÿßŒœĀāĒēĪīŌōŪūȲȳ"
  + "0123456789'’-/(),.:;=?! ";
// The reader's settings, ONE copy: the phone and scripts/check-ocr.mjs both use it, so the
// fixtures measure exactly what a parent's photo gets.
export const OCR_PARAMS = { tessedit_pageseg_mode: OCR_PSM, preserve_interword_spaces: "1", tessedit_char_whitelist: OCR_CHARS };

// ---- 1. clean-up ----------------------------------------------------------------------------

function integralImage(gray, w, h) {
  // (w+1)*(h+1) so a window sum is four lookups with no edge branches.
  const W = w + 1;
  const I = new Float64Array(W * (h + 1));
  for (let y = 1; y <= h; y++) {
    let row = 0;
    const base = y * W; const src = (y - 1) * w;
    for (let x = 1; x <= w; x++) { row += gray[src + x - 1]; I[base + x] = I[base - W + x] + row; }
  }
  return I;
}
function windowMean(I, w, h, x, y, r) {
  const W = w + 1;
  const x0 = Math.max(0, x - r), y0 = Math.max(0, y - r), x1 = Math.min(w, x + r + 1), y1 = Math.min(h, y + r + 1);
  const sum = I[y1 * W + x1] - I[y0 * W + x1] - I[y1 * W + x0] + I[y0 * W + x0];
  return sum / ((x1 - x0) * (y1 - y0));
}

export function toGray(data, w, h) {
  const g = new Float32Array(w * h);
  for (let i = 0, p = 0; i < w * h; i++, p += 4) g[i] = 0.299 * data[p] + 0.587 * data[p + 1] + 0.114 * data[p + 2];
  return g;
}

// A ruled row is one where ink runs nearly the whole way across; a ruled column likewise down.
// Text never does that: a line of words covers a fifth of its row at most. Runs are measured
// rather than pixels counted, so a dense line of type cannot be mistaken for a rule.
// Returns the rules' centres, and erases the runs that make them from `ink`.
export function findAndEraseRules(ink, w, h) {
  const rows = []; const cols = [];
  const erase = [];
  // BOTH are measured on the untouched image before either is erased. Rubbing the horizontal
  // rules out first cuts every vertical rule into one short piece per row, and no column is
  // ever found again — which is exactly how three real Latin tables came back with no columns.
  // A printed rule comes off the threshold faintly dashed, so a few blank pixels inside it must
  // not end the run — insisting on an unbroken one found 4 rules on a 20-row spelling list.
  const minRun = Math.max(24, Math.round(w * 0.35));
  // How thick a rule may be. A pen or a thick print line on a photograph is several pixels
  // across, and the old fixed one-or-two either side left its edge behind: Tesseract read that
  // stub as "|" leading almost every cell of real-latin-nouns (Alan, 4 Oct: lookalikes across
  // the board). Each rule pixel is now followed outward through the ink JOINED to it, up to
  // this cap, so the whole stroke goes and a letter that merely touches the rule keeps all but
  // its edge.
  const maxT = Math.max(3, Math.round(Math.max(w, h) / 250));
  const hGap = Math.max(2, Math.round(w / 200));
  for (let y = 0; y < h; y++) {
    let run = 0; let gap = 0; let best = 0; let bestEnd = 0; let end = 0;
    for (let x = 0; x < w; x++) {
      if (ink[y * w + x]) { if (gap) { run += gap; gap = 0; } run++; end = x; if (run > best) { best = run; bestEnd = end; } }
      else if (run) { gap++; if (gap > hGap) { run = 0; gap = 0; } }
    }
    // A rule is two or three pixels thick; leaving one of them behind gives Tesseract a stub it
    // reads as a letter — "filia" came back "lfitia" and "nauta" "lnauta". Erase a pixel either
    // side of the run as well.
    if (best >= minRun) { rows.push(y); for (let x = bestEnd - best + 1; x <= bestEnd; x++) for (let d = -1; d <= 1; d++) { const yy = y + d; if (yy >= 0 && yy < h && ink[yy * w + x]) erase.push(yy * w + x); } }
  }
  // A vertical rule on a PHOTOGRAPH is never in one pixel column: the sheet is at a slight
  // angle, so the line drifts sideways as it goes down. The line is followed instead, allowed
  // one pixel of drift a row, and a short break (where a horizontal rule crosses it) is stepped
  // over rather than ending the run.
  const minCol = Math.max(24, Math.round(h * 0.25));
  const gapOk = Math.max(4, Math.round(h / 120));
  const claimed = new Uint8Array(w);
  for (let x0 = 0; x0 < w; x0++) {
    if (claimed[x0]) continue;
    let cur = x0; let run = 0; let gap = 0; let path = []; let best = 0; let trail = [];
    for (let y = 0; y < h; y++) {
      let at = -1;
      for (const dx of [0, -1, 1]) { const nx = cur + dx; if (nx >= 0 && nx < w && ink[y * w + nx]) { at = nx; break; } }
      if (at < 0) {
        gap++;
        if (gap > gapOk) { if (run > best) { best = run; trail = path; } run = 0; gap = 0; path = []; }
        continue;
      }
      gap = 0; cur = at; run++; path.push(y * w + at);
    }
    if (run > best) { best = run; trail = path; }
    if (best >= minCol) {
      const xs = trail.map((p) => p % w);
      const spread = Math.max(...xs) - Math.min(...xs);
      // A rule is straight. One pixel of drift a row is allowed above, and over a long run that
      // freedom lets the follow wander out of the rule and into a column of text — which then
      // gets erased, and a whole table's words with it. A candidate that has wandered further
      // than a photograph's skew can explain is not a rule.
      if (spread > Math.max(6, Math.round(w / 40))) continue;
      const mid = Math.round((Math.min(...xs) + Math.max(...xs)) / 2);
      cols.push(mid);
      // The rule's own thickness is measured, not assumed: the joined ink either side of each
      // point on it, and the median of those widths is how far it is followed. A letter touching
      // the rule makes that point wider than the median, and loses only the rule's width of it.
      const reach = (px, py, dir) => { let d = 0; while (d < maxT * 3) { const nx = px + dir * (d + 1); if (nx < 0 || nx >= w || !ink[py * w + nx]) break; d++; } return d; };
      const spans = trail.map((p) => { const px = p % w, py = (p / w) | 0; return [px, py, reach(px, py, -1), reach(px, py, 1)]; });
      const widths = spans.map(([, , l, r]) => l + r + 1).sort((a, b) => a - b);
      const m = Math.min(maxT, widths[widths.length >> 1] || 1);
      for (const [px, py, l, r] of spans) for (let d = -Math.min(l, m); d <= Math.min(r, m); d++) erase.push(py * w + px + d);
      for (let d = -2; d <= 2; d++) if (mid + d >= 0 && mid + d < w) claimed[mid + d] = 1;
    }
  }
  for (const p of erase) ink[p] = 0;
  // A drawn line is several pixels thick and shows up as a handful of adjacent rows: one rule.
  // A drawn rule is a few pixels thick and shows up as a handful of adjacent rows: one rule. A
  // BAND of adjacent rows far thicker than that is not a rule at all — it is a line of large
  // type whose letters happen to run across, which is the price of looking for a shorter run.
  const merge = (v, gap, maxThick) => {
    const o = []; let run = [];
    const flush = () => { if (!run.length) return; const thick = run[run.length - 1] - run[0] + 1; if (!maxThick || thick <= maxThick) o.push(Math.round((run[0] + run[run.length - 1]) / 2)); run = []; };
    for (const a of v) { if (run.length && a - run[run.length - 1] > gap) flush(); run.push(a); }
    flush();
    return o;
  };
  return {
    rows: merge(rows, Math.max(3, Math.round(h / 120)), Math.max(6, Math.round(h / 90))),
    cols: merge(cols.sort((a, b) => a - b), Math.max(3, Math.round(w / 120)), 0),
  };
}

// Grain left by the threshold. Without this Tesseract merges a row into one huge low-confidence
// box; with it the same words come back boxed and scored in the nineties. Anything smaller than
// a piece of punctuation goes: a full stop on a phone photo of a sheet is ~1% of the height.
export function despeckle(ink, w, h) {
  const minSide = Math.max(3, Math.round(h / 140));
  const minArea = Math.max(8, Math.round((w * h) / 90000));
  const seen = new Uint8Array(w * h);
  const stack = new Int32Array(4096);
  const blob = [];
  for (let i = 0; i < w * h; i++) {
    if (!ink[i] || seen[i]) continue;
    let sp = 0; stack[sp++] = i; seen[i] = 1; blob.length = 0;
    let x0 = i % w, x1 = x0, y0 = (i / w) | 0, y1 = y0;
    while (sp > 0) {
      const p = stack[--sp]; blob.push(p);
      const px = p % w, py = (p / w) | 0;
      if (px < x0) x0 = px; if (px > x1) x1 = px;
      if (py < y0) y0 = py; if (py > y1) y1 = py;
      for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) {
        const nx = px + dx, ny = py + dy;
        if (nx < 0 || ny < 0 || nx >= w || ny >= h) continue;
        const q = ny * w + nx;
        if (ink[q] && !seen[q]) { seen[q] = 1; if (sp < stack.length) stack[sp++] = q; }
      }
    }
    if ((x1 - x0 + 1) < minSide && (y1 - y0 + 1) < minSide && blob.length < minArea) for (const p of blob) ink[p] = 0;
  }
}

// Shadow removal: the local background is a wide mean; dividing by it flattens a shadow or a
// gradient across the page. Then an adaptive threshold: a pixel is ink when it is darker than
// its neighbourhood by more than `c`. Then the table's rules are found and erased and the grain
// dropped (the real-sheet round, above). Returns a new RGBA buffer, pure black on white, and
// `rules` — the geometry parseSheetCells reads the sheet with.
export function preprocessImage({ data, width: w, height: h }, { bgRadius = null, thrRadius = null, c = 12, rules = true } = {}) {
  const gray = toGray(data, w, h);
  const I = integralImage(gray, w, h);
  const rb = bgRadius || Math.max(16, Math.round(Math.max(w, h) / 20));
  const norm = new Float32Array(w * h);
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
    const bg = windowMean(I, w, h, x, y, rb);
    norm[y * w + x] = Math.min(255, (gray[y * w + x] / Math.max(bg, 1)) * 255);
  }
  const I2 = integralImage(norm, w, h);
  const rt = thrRadius || Math.max(8, Math.round(Math.max(w, h) / 60));
  const ink = new Uint8Array(w * h);
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
    const i = y * w + x;
    if (norm[i] < windowMean(I2, w, h, x, y, rt) - c) ink[i] = 1;
  }
  let found = { rows: [], cols: [] };
  if (rules) { found = findAndEraseRules(ink, w, h); despeckle(ink, w, h); }
  const out = new Uint8ClampedArray(w * h * 4);
  for (let i = 0; i < w * h; i++) {
    const v = ink[i] ? 0 : 255;
    out[i * 4] = v; out[i * 4 + 1] = v; out[i * 4 + 2] = v; out[i * 4 + 3] = 255;
  }
  return { data: out, width: w, height: h, rules: found };
}

// ---- 2. the reader (browser only) ------------------------------------------------------------

let readerPromise = null;

function loadScriptOnce(src) {
  return new Promise((resolve, reject) => {
    if (typeof document === "undefined") { reject(new Error("no document")); return; }
    if (document.querySelector(`script[src="${src}"]`) && window.Tesseract) { resolve(); return; }
    const s = document.createElement("script");
    s.src = src; s.async = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error(`could not load ${src}`));
    document.head.appendChild(s);
  });
}

// WebAssembly SIMD is on every current phone browser (iOS 16.4+, Chrome 91+); the plain core
// is the fallback for anything older. Same probe wasm-feature-detect uses.
export function wasmSimd() {
  try {
    return WebAssembly.validate(new Uint8Array([0, 97, 115, 109, 1, 0, 0, 0, 1, 5, 1, 96, 0, 1, 123, 3, 2, 1, 0, 10, 10, 1, 8, 0, 65, 0, 253, 15, 253, 98, 11]));
  } catch { return false; }
}

// One worker per page, made on first use. `onProgress(stage, fraction)` for the reading screen.
export async function loadReader(onProgress) {
  if (!readerPromise) {
    readerPromise = (async () => {
      onProgress && onProgress("loading", 0.05);
      await loadScriptOnce(OCR_VENDOR.script);
      const T = window.Tesseract;
      if (!T || !T.createWorker) throw new Error("reader missing");
      const worker = await T.createWorker("eng", T.OEM.LSTM_ONLY, {
        workerPath: OCR_VENDOR.worker,
        corePath: wasmSimd() ? OCR_VENDOR.coreSimd : OCR_VENDOR.coreBasic,
        langPath: OCR_VENDOR.langPath,
        gzip: true,
        cacheMethod: "none", // the browser's HTTP cache keeps the model; no IndexedDB copy to go stale
        logger: (m) => {
          if (!onProgress) return;
          if (m.status === "loading tesseract core") onProgress("loading", 0.1 + 0.2 * (m.progress || 0));
          else if (m.status === "loading language traineddata") onProgress("loading", 0.3 + 0.3 * (m.progress || 0));
          else if (m.status === "recognizing text") onProgress("reading", 0.7 + 0.3 * (m.progress || 0));
        },
      });
      await worker.setParameters(OCR_PARAMS);
      return worker;
    })().catch((e) => { readerPromise = null; throw e; });
  }
  return readerPromise;
}

// Reads a cleaned image (a canvas, blob or ImageData) and returns BOTH what Tesseract saw:
// `lines` for the plain path and `words` with their boxes for the geometric one. The boxes are
// asked for with { blocks: true } — the vendored worker's own option, off by default and free,
// the recognition having happened either way.
export async function readImage(image, onProgress) {
  const worker = await loadReader(onProgress);
  onProgress && onProgress("reading", 0.7);
  const { data } = await worker.recognize(image, {}, { text: true, blocks: true });
  return { lines: linesFromText(data && data.text ? data.text : ""), words: wordsFromBlocks(data && data.blocks) };
}

// blocks → paragraphs → lines → words, flattened to { x, y, w, h, text, conf }.
export function wordsFromBlocks(blocks) {
  const out = [];
  for (const b of blocks || []) for (const p of b.paragraphs || []) for (const l of p.lines || []) for (const wd of l.words || []) {
    const bb = wd.bbox || {};
    const text = String(wd.text || "").trim();
    if (!text) continue;
    out.push({ x: bb.x0 | 0, y: bb.y0 | 0, w: (bb.x1 - bb.x0) | 0, h: (bb.y1 - bb.y0) | 0, text, conf: Number(wd.confidence) || 0 });
  }
  return out;
}

export function linesFromText(text) {
  return String(text || "").split(/\r?\n/).map((l) => l.replace(/\s+/g, " ").trim()).filter(Boolean);
}

// ---- 3. what the sheet says -------------------------------------------------------------------

// Tesseract's usual slips inside a word: a digit where a letter is. Only inside a token that
// is otherwise letters, so a real number ("Year 4") is left alone.
export function fixOcrSlips(token) {
  if (!/[a-zA-Z]/.test(token)) return token;
  if (!/^[a-zA-Z0-9|'’-]+$/.test(token)) return token;
  if (!/[0-9|]/.test(token)) return token;
  const letters = token.replace(/[0-9|]/g, "").length;
  if (letters < token.length / 2) return token;
  return token.replace(/1|\|/g, "l").replace(/0/g, "o").replace(/5/g, "s").replace(/8/g, "B");
}

// The pronoun I (Alan, 3 Oct, a Latin verb sheet: "It stripped most of the I. I had to manually
// add them"; the ones it kept came out as "l attack", which the app's font draws exactly like
// "I attack", so neither the word check nor the child could see why it was marked wrong).
// Tesseract reads a lone capital I as "l", or with so little confidence that the word filter
// below threw it away. A verb table has it on nearly every row. A lone I or l leading a cell,
// with a word after it, is the pronoun: nothing else on a list looks like that.
// NOT a lone "|": on every real sheet in tests/fixtures/ocr that is the table's own ruled line,
// read at the start of a cell (it sits on the column rule and stands taller than the text), so
// it stays dropped. A pronoun read as a bar is left to the word check.
const I_SHAPE = /^[Il]$/;
export function fixLonePronoun(cell) {
  return String(cell || "").replace(/^l(?=\s+[A-Za-z]{2,})/, "I");
}

// The spelling on a row of a spelling sheet (Alan, 7 Oct, a doubling-consonants sheet with
// every word highlighted in green: "This photo is going through tesseract really badly"). The
// highlighter's tail came back as stray marks around the word: "worried.", "hopped,", "reply s",
// "x hopped", "3 travel 4", and "I 12) carried" where the numbering shared the cell. The line
// path already took the first alphabetic token; the cell path took the first TOKEN, punctuation
// and all. Both now take the first token with three or more letters, punctuation stripped, and
// fall back to the first alphabetic one, so a stray one-letter mark before the word never
// stands in for it. Two letters or fewer is never a spelling (see parseSheetLines).
export function spellingWordFrom(text) {
  const found = [...String(text || "").matchAll(/[A-Za-z][A-Za-z0-9|'’-]*/g)].map((m) => fixOcrSlips(m[0]));
  return found.find((w) => w.replace(/[^A-Za-z]/g, "").length >= 3) || found[0] || "";
}

const HEADING = /^(week|unit|chapter|term|year|class|name|date|spelling|spellings|vocab|vocabulary|list|words|test|homework|practice|practise|learn|write|cover|check|look|say|copy|tick|signed)\b/i;

// Lines → the words or pairs a list is made of.
//   spelling: one word per row — the first alphabetic token after any numbering; everything
//             after it is the write-then-cover columns (the trial's rule) and is dropped
//   english / language: the paste parser's pair rules, after numbering is removed
// Returns { pairs, dropped, droppedWhy } like parseHomeworkText (droppedWhy here also says
// "heading", "short" or "repeat"), plus `lines` (what was read) for the
// review screen's "raw" toggle.
export function parseSheetLines(lines, type) {
  const cleaned = [];
  const dropped = [];
  const droppedWhy = [];
  for (const raw of lines || []) {
    let line = String(raw).trim();
    if (!line) continue;
    line = line.replace(/^[\s\u2022\u2023\u25E6\u2043\u2219*·—–\-]+/, "").replace(/^\(?\d{1,3}\s*[.):\-]\s*/, "").trim();
    if (!line) { dropped.push(raw); droppedWhy.push("empty"); continue; }
    if (HEADING.test(line) && !/[,\t:=|]|\s[-–—]\s/.test(line)) { dropped.push(raw); droppedWhy.push("heading"); continue; }
    if (type === "spelling") {
      const word = spellingWordFrom(line);
      // Two letters or fewer is never a spelling — it is a column heading fragment ("SS")
      // or a stray mark. A word already read is, on THIS path, the write-then-cover column
      // repeating it: with no geometry there is nothing to tell a real repeat from an echo, and
      // the echo is far commoner, so it goes to the ignored list where the parent can add it
      // back. A sheet read off its rules keeps its repeats instead — see parseSheetCells.
      if (!word || word.replace(/[^A-Za-z]/g, "").length < 3) { dropped.push(raw); droppedWhy.push(word ? "short" : "empty"); continue; }
      if (cleaned.some((c) => c.toLowerCase() === word.toLowerCase())) { dropped.push(raw); droppedWhy.push("repeat"); continue; }
      cleaned.push(word);
    } else {
      const tokens = line.split(/(\s+)/).map((t) => (/\s/.test(t) ? t : fixOcrSlips(t))).join("");
      cleaned.push(tokens);
    }
  }
  if (type === "spelling") return { pairs: cleaned.map((a) => ({ a, b: "" })), dropped, droppedWhy, lines: lines || [] };
  const parsed = parseHomeworkText(cleaned.join("\n"), type);
  const pairs = parsed.pairs.map((p) => ({ ...p, a: fixLonePronoun(p.a), b: fixLonePronoun(p.b) }));
  return { pairs, dropped: dropped.concat(parsed.dropped), droppedWhy: droppedWhy.concat(parsed.droppedWhy), lines: lines || [] };
}

// A word that is already in the list used to be thrown away as the write-then-cover column
// repeating itself. Real sheets repeat words on purpose — "desperate" is twice on the
// unstressed-vowels list — and silently losing one is worse than showing it. Where the sheet's
// own rules say the two are in DIFFERENT ROWS, it is kept and marked, and the review screen
// puts it in front of the parent to delete or keep.
export function markRepeats(pairs) {
  const seen = new Set();
  return (pairs || []).map((p) => {
    const k = String(p.a || "").trim().toLowerCase();
    if (!k) return p;
    if (seen.has(k)) return { ...p, repeat: true };
    seen.add(k);
    return p;
  });
}

// ---- 3b. what the sheet says, read off the table's own geometry --------------------------------

// A word worth placing. Confidence alone must not decide: "amamus" was read exactly right and
// scored 0 because a speck beside it was taken for an apostrophe. A plainly alphabetic word of
// three letters or more is kept whatever its score, and the word check flags it if it is wrong.
// A lone I-shaped mark is kept here whatever its score; cellGrid keeps it only where it leads a
// cell with a word after it (fixLonePronoun), and drops it anywhere else, as before.
function usableWord(wd) {
  const t = String(wd.text || "").trim();
  if (I_SHAPE.test(t)) return true;
  if (!t || !/[A-Za-z0-9]/.test(t)) return false;
  if (wd.conf > 15) return true;
  return t.replace(/[^A-Za-z]/g, "").length >= 3;
}

// The words inside one cell, as they read: lines top to bottom by vertical overlap, words left
// to right inside a line. This is what puts a wrapped cell back together.
export function cellText(items) {
  return orderedItems(items).map((i) => i.text).join(" ").replace(/\s+/g, " ").trim();
}

function orderedItems(items) {
  const lines = [];
  for (const it of [...items].sort((a, b) => a.y - b.y)) {
    const mid = it.y + it.h / 2;
    const ln = lines.find((l) => mid >= l.y0 && mid <= l.y1);
    if (ln) ln.items.push(it);
    else lines.push({ y0: it.y, y1: it.y + it.h, items: [it] });
  }
  return lines.flatMap((l) => l.items.sort((a, b) => a.x - b.x));
}

// A cell's text, with an I-shaped mark kept only where it is the pronoun: first, with a word
// after it. One the old filter would have dropped (low confidence) is dropped anywhere else, so
// a speck in a cell never becomes a word.
function cellTextWithPronoun(items) {
  const weak = (it) => I_SHAPE.test(String(it.text).trim()) && it.conf <= 15;
  const ordered = orderedItems(items);
  const kept = ordered.filter((it, i) => !weak(it) || (i === 0 && ordered[1] && /[A-Za-z]{2,}/.test(ordered[1].text)));
  return fixLonePronoun(kept.map((i) => i.text).join(" ").replace(/\s+/g, " ").trim());
}

// Words → a grid. Rows are the bands between the horizontal rules, columns the gaps between the
// vertical ones. The image EDGES are boundaries too: the first and last row of a table are
// bounded by the crop, not by a rule, and without this they fall outside every band and vanish.
export function cellGrid(words, rules, width, height) {
  const rows = (rules && rules.rows) || [];
  const cols = (rules && rules.cols) || [];
  const edges = [...new Set([0, ...rows, height])].sort((a, b) => a - b);
  const usable = (words || []).filter(usableWord);
  const out = [];
  for (let i = 0; i < edges.length - 1; i++) {
    const y0 = edges[i], y1 = edges[i + 1];
    if (y1 - y0 < height / 80) continue;
    const cells = [];
    for (const wd of usable) {
      const cy = wd.y + wd.h / 2;
      if (cy < y0 || cy >= y1) continue;
      const c = cols.filter((v) => wd.x + wd.w / 2 > v).length;
      (cells[c] || (cells[c] = [])).push(wd);
    }
    if (!cells.length) continue;
    const text = [];
    for (let c = 0; c < cells.length; c++) text[c] = cells[c] ? cellTextWithPronoun(cells[c]) : "";
    if (text.some((t) => t)) out.push(text);
  }
  return out;
}

const NUMBER_ONLY = /^[\s(]*\d{1,3}\s*(st|nd|rd|th|[.):\-])?\s*$/i;

// Which columns carry the list. A sheet has furniture beside the words — a numbering column, a
// "sg / pl" column, an empty Practice column for the child to write in — and the list is the
// column that has a word on most rows and is not just numbers.
export function contentColumns(grid) {
  const n = Math.max(0, ...grid.map((r) => r.length));
  const scored = [];
  for (let c = 0; c < n; c++) {
    let wordy = 0, numeric = 0;
    for (const r of grid) {
      const v = String(r[c] || "");
      // Three letters, not two: a Latin table's "sg" and "pl" column has a word on most rows and
      // would otherwise be taken for the list, putting "sg" where "amo" belongs.
      if (/[A-Za-z]{3,}/.test(v)) wordy++;
      if (NUMBER_ONLY.test(v)) numeric++;
    }
    if (grid.length && wordy / grid.length >= 0.5 && numeric / grid.length < 0.5) scored.push([c, wordy]);
  }
  // At most two columns are the list — a term and its meaning. Where more qualify (a sheet with
  // a Practice column the child has written in), the fullest two win, leftmost first.
  const best = scored.sort((a, b) => b[1] - a[1] || a[0] - b[0]).slice(0, 2).map(([c]) => c).sort((a, b) => a - b);
  return best;
}

// The grid → the words or pairs a list is made of, with the same heading, slip and repeat rules
// as the plain path. Returns the same shape as parseSheetLines.
export function parseSheetCells(grid, type) {
  const keep = contentColumns(grid);
  const dropped = []; const droppedWhy = []; const lines = [];
  const pairs = [];
  const termCol = keep[0];
  const meaningCol = keep.length > 1 ? keep[1] : null;
  for (const row of grid) {
    const joined = row.filter(Boolean).join(" ").trim();
    if (joined) lines.push(joined);
    if (termCol === undefined) continue;
    const rawTerm = String(row[termCol] || "").trim();
    if (!rawTerm) { if (joined) { dropped.push(joined); droppedWhy.push("empty"); } continue; }
    if (HEADING.test(rawTerm) && !/[,\t:=|]|\s[-–—]\s/.test(rawTerm)) { dropped.push(joined); droppedWhy.push("heading"); continue; }
    const term = rawTerm.split(/\s+/).map(fixOcrSlips).join(" ").replace(/^[^A-Za-z]+/, "").trim();
    if (term.replace(/[^A-Za-z]/g, "").length < 2) { dropped.push(joined); droppedWhy.push(term ? "short" : "empty"); continue; }
    if (type === "spelling") { const word = spellingWordFrom(term); if (word.replace(/[^A-Za-z]/g, "").length < 3) { dropped.push(joined); droppedWhy.push("short"); continue; } pairs.push({ a: word, b: "" }); continue; }
    const rawMean = meaningCol === null ? "" : String(row[meaningCol] || "").trim();
    const mean = rawMean.split(/\s+/).map(fixOcrSlips).join(" ").replace(/^[^A-Za-z(]+/, "").trim();
    if (!mean) { dropped.push(joined); droppedWhy.push("nopair"); continue; }
    pairs.push({ a: term, b: mean });
  }
  return { pairs: markRepeats(pairs), dropped, droppedWhy, lines };
}

// The one entry point PhotoCapture calls. A sheet with rules is read off its geometry, which is
// the only way a wrapped cell comes back whole; a plain typed list has no rules and goes through
// the line path. When a sheet has rules but the geometry reads FEWER rows than the plain path
// (a photograph where the rules are half-detected), the plain path wins — the two are compared
// rather than one being trusted. Clearly fewer: more than a tenth.
export function parseSheet({ lines = [], words = [], rules = null, width = 0, height = 0 }, type) {
  const byLine = parseSheetLines(lines, type);
  const rows = (rules && rules.rows) || [];
  if (rows.length < 3 || !words.length || !height) return { ...byLine, how: "lines" };
  const grid = cellGrid(words, rules, width, height);
  const byCell = parseSheetCells(grid, type);
  // "Clearly fewer", not one fewer (4 Oct): with the rules erased properly, real-prefixes read
  // 19 of 20 off its table, and lost to a line read that had ONE more row only because it kept
  // three stray words from the sheet's heading. A half-detected table loses whole rows.
  // Alan, 7 Oct (a doubling-consonants sheet, every word highlighted): the line read now keeps a
  // word a stray mark used to swallow ("f illiterate"), so on real-prefixes it grew to 25 rows,
  // five of them the sheet's furniture ("wee", "eat" from the write column, the heading and the
  // footer), and the table read of 21 lost to it on the count alone. A table whose rules were
  // found for nearly every line (a band for at least four fifths of the rows the line read
  // found) is a complete table, and the geometry is the better reader of it: it is what drops
  // the furniture, since a stray in a band is a trailing token, not a row. The count comparison
  // below is kept for the other case, a half-detected table.
  if (rows.length + 1 >= byLine.pairs.length * 0.8) return { ...byCell, how: "cells" };
  if (byCell.pairs.length < byLine.pairs.length * 0.9) return { ...byLine, how: "lines" };
  return { ...byCell, how: "cells" };
}

// ---- 4. the word check ----------------------------------------------------------------------

let dictPromise = null;

export function makeDictionary(text) {
  const words = new Set();
  const byKey = new Map(); // first letter + length → [words], for fast suggestions
  for (const raw of String(text || "").split(/\r?\n/)) {
    const w = raw.trim().toLowerCase();
    if (!w) continue;
    words.add(w);
    const k = w[0] + w.length;
    if (!byKey.has(k)) byKey.set(k, []);
    byKey.get(k).push(w);
  }
  return { words, byKey, size: words.size };
}

let dictLoaded = null;
export async function loadDictionary() {
  if (!dictPromise) {
    dictPromise = fetch(OCR_VENDOR.dictionary).then((r) => { if (!r.ok) throw new Error(`dictionary ${r.status}`); return r.text(); }).then(makeDictionary)
      .then((d) => { dictLoaded = d; return d; })
      .catch((e) => { dictPromise = null; throw e; });
  }
  return dictPromise;
}

// The dictionary ONLY if it is already in memory, never fetching it (CC, 27 Sep). Swapping the
// columns of a list re-runs the word check — but on a TYPED list that meant downloading 660 kB
// to second-guess words the parent had just typed themselves, and only if they happened to press
// Swap, so the same list showed no amber before the tap and amber after it. The check belongs to
// the photo path, where the dictionary is already loaded and the re-check is instant. A typed
// list has nothing loaded, gets nothing fetched, and stays as the parent wrote it.
export function dictionaryIfLoaded() { return dictLoaded; }

// Damerau-Levenshtein, capped: returns max+1 as soon as the distance cannot be ≤ max.
export function editDistance(a, b, max = 2) {
  if (Math.abs(a.length - b.length) > max) return max + 1;
  const m = a.length, n = b.length;
  let prev2 = null; let prev = new Array(n + 1); for (let j = 0; j <= n; j++) prev[j] = j;
  for (let i = 1; i <= m; i++) {
    const cur = new Array(n + 1); cur[0] = i; let rowMin = cur[0];
    for (let j = 1; j <= n; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      let v = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + cost);
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) v = Math.min(v, prev2[j - 2] + 1);
      cur[j] = v; if (v < rowMin) rowMin = v;
    }
    if (rowMin > max) return max + 1;
    prev2 = prev; prev = cur;
  }
  return prev[n];
}

// The dictionary word closest to `word` (distance ≤ 2), or null. Same first letter is tried
// first because OCR rarely loses the first letter of a printed word; then the next letters of
// the alphabet's neighbours are not tried at all — a suggestion should be obvious, not clever.
// Tesseract's lookalikes (Alan, 4 Oct: "what steps can we take to stop it doing this across
// the board"). Most of its mistakes are one real letter, or two, drawn like another: rn for m,
// cl for d, l for I. A character limit cannot stop those (l is a letter), and edit distance alone
// ranks them badly: "rnother" is two edits from "mother" and one from other words. So a
// lookalike swap is tried FIRST, and kept only when exactly one swap makes a real word. It is a
// suggestion like any other: the parent taps it, nothing is changed for them.
const LOOKALIKES = [["rn", "m"], ["m", "rn"], ["cl", "d"], ["d", "cl"], ["vv", "w"], ["w", "vv"], ["li", "h"], ["ri", "n"],
  ["l", "i"], ["i", "l"], ["l", "t"], ["t", "l"], ["e", "c"], ["c", "e"], ["n", "u"], ["u", "n"], ["h", "b"], ["b", "h"]];
export function lookalikeFix(word, dict) {
  // A capital I after a lowercase letter is an l: "girI", "wiII". Read before lowercasing, which
  // would turn it into an i and lose the clue.
  const raw = String(word || "").replace(/\s+/g, "");
  if (/[a-z]I/.test(raw)) { const c = normaliseAnswer(raw.replace(/(?<=[a-zI])I/g, "l")); if (dict && dict.words.has(c)) return c; }
  const w = normaliseAnswer(word).replace(/\s+/g, "");
  if (!w || !dict || dict.words.has(w)) return null;
  const found = new Set();
  for (const [from, to] of LOOKALIKES) {
    for (let i = w.indexOf(from); i >= 0; i = w.indexOf(from, i + 1)) {
      const c = w.slice(0, i) + to + w.slice(i + from.length);
      if (dict.words.has(c)) found.add(c);
    }
  }
  return found.size === 1 ? [...found][0] : null;
}

export function suggestWord(word, dict) {
  const w = normaliseAnswer(word).replace(/\s+/g, "");
  if (!w || !dict) return null;
  const look = lookalikeFix(w, dict);
  if (look) return look;
  const candidates = [];
  const first = w[0];
  for (const len of [w.length, w.length - 1, w.length + 1]) {
    const list = dict.byKey.get(first + len);
    if (list) for (const c of list) { const d = editDistance(w, c, 2); if (d <= 2) candidates.push([d, c]); }
  }
  if (!candidates.length) {
    // First letter misread (rn→m, l→1 fixed already): try every first letter at the same length.
    for (const [k, list] of dict.byKey) {
      if (Number(k.slice(1)) !== w.length) continue;
      for (const c of list) { if (editDistance(w, c, 1) <= 1) candidates.push([1.5, c]); }
    }
  }
  if (!candidates.length) return null;
  candidates.sort((x, y) => x[0] - y[0] || Math.abs(x[1].length - w.length) - Math.abs(y[1].length - w.length) || x[1].localeCompare(y[1]));
  return candidates[0][1];
}

// LB-378/379 (Alan, 27 Sep, from a photographed Latin sheet): a vocabulary sheet is as likely
// to print the foreign language first as second, and the reader assumed English was column A.
// Everything downstream is built on that assumption — the word check runs on A — so a Latin-first
// sheet had every Latin word checked against an English dictionary and came back with a page of
// amber: cena offered "cent", dea offered "deb", epistula offered "epistle".
//
// The dictionary that is already loaded for the check settles it. English words are in it and a
// foreign column's are not, so the side with the higher share of known words is the English one.
// This is the same idea as homework.js's looksSwapped, which works off a short hint list because
// that is all a typed list has; here the whole of SCOWL is in memory at that moment, so it is
// used instead. Only a CONFIDENT call swaps: the other side must clearly beat A, which leaves a
// list of proper nouns or one the dictionary knows both halves of exactly as it was read.
export function englishShare(cells, dict) {
  let hits = 0; let words = 0;
  for (const c of cells || []) for (const w of String(c || "").toLowerCase().split(/[^a-z']+/)) {
    if (w.length < 2) continue;
    words += 1;
    if (dict && dict.words && dict.words.has(w)) hits += 1;
  }
  return words ? hits / words : 0;
}

// Returns the pairs with English in column A, and whether they had to be turned round.
export function englishFirst(pairs, dict) {
  const rows = (pairs || []).filter((p) => p && p.a && p.b);
  if (rows.length < 2 || !dict || !dict.words || !dict.size) return { pairs: pairs || [], swapped: false };
  const a = englishShare(rows.map((p) => p.a), dict);
  const b = englishShare(rows.map((p) => p.b), dict);
  if (!(b >= 0.5 && b > a + 0.2)) return { pairs, swapped: false };
  return { pairs: pairs.map((p) => ({ ...p, a: p.b, b: p.a })), swapped: true };
}

// Two words read as one (Alan, 27 Sep: "ensure that a space is added between words"). Tesseract
// returns a wrapped or tightly-set cell as a single token — "We love" comes back "Welove" — and a
// child would then be practising a word that does not exist. Only a token the dictionary does NOT
// know is tried, and only a split where BOTH halves are words it knows, so an ordinary long word
// is never pulled apart. Like every other suggestion this is OFFERED and never applied: the
// parent taps it or types over it.
export function splitRunTogether(word, dict) {
  const w = String(word || "").toLowerCase();
  if (!dict || !dict.words || w.length < 5 || !/^[a-z]+$/.test(w) || dict.words.has(w)) return null;
  for (let i = 2; i <= w.length - 2; i++) {
    const a = w.slice(0, i); const b = w.slice(i);
    // A misread can split into something a child should not be shown: "He/she/it" mangled to
    // "heshit" would otherwise be offered as two words. Never suggested, on a list that is
    // shareable (Alan, 27 Sep).
    if (dict.words.has(a) && dict.words.has(b) && !containsRudeWord(`${a} ${b}`)) return `${a} ${b}`;
  }
  return null;
}

// For each English word: { word, ok, suggestion }. `known` is a set of words the household
// already uses (its lists) so a family's own vocabulary is never flagged. Words with a space
// (a two-word answer) are checked word by word.
export function checkWords(words, dict, known = new Set()) {
  return (words || []).map((word) => {
    const w = String(word || "").trim();
    if (!w) return { word: w, ok: true, suggestion: null };
    // A bracketed note is the sheet's grammar ("You (sg.) love"), not a word to spell: marking
    // already treats it as optional (homework.js answerForms), so it is not checked either.
    const parts = normaliseAnswer(w.replace(/\([^)]*\)/g, " ")).split(" ").filter(Boolean);
    // A capital I inside a lowercase word ("girI") is an l, even where the lowercased reading
    // happens to be in the dictionary ("giri") and would otherwise pass unflagged.
    const rawParts = w.replace(/\([^)]*\)/g, " ").split(/\s+/).filter(Boolean);
    // So is a lone l or | standing as a word ("l attack"): the pronoun, read as its lookalike.
    if (rawParts.length === parts.length && rawParts.some((rp) => /[a-z]I/.test(rp) || /^[l|]$/.test(rp))) {
      const fixes = rawParts.map((rp) => (/^[l|]$/.test(rp) ? "I" : /[a-z]I/.test(rp) ? lookalikeFix(rp, dict) : null));
      if (fixes.some(Boolean)) return { word: w, ok: false, suggestion: parts.map((p, i) => fixes[i] || p).join(" ") };
    }
    const bad = parts.filter((p) => /^[a-z'-]+$/.test(p) && !dict.words.has(p) && !known.has(p) && !(p.length <= 1));
    if (!bad.length) return { word: w, ok: true, suggestion: null };
    // A run-together pair is tried FIRST: "Welove" has no near neighbour as one word, so the
    // edit-distance search would give up and the parent would be left to spot it themselves.
    if (parts.length === 1) {
      const look = lookalikeFix(parts[0], dict);
      if (look) return { word: w, ok: false, suggestion: look };
      const split = splitRunTogether(parts[0], dict);
      if (split) return { word: w, ok: false, suggestion: split };
      const s = suggestWord(parts[0], dict);
      return { word: w, ok: false, suggestion: s && s !== parts[0] && !containsRudeWord(s) ? s : null };
    }
    // a phrase: suggest the phrase with its bad words replaced, only if every bad word has a suggestion
    const fixed = parts.map((p) => (bad.includes(p) ? suggestWord(p, dict) : p));
    return { word: w, ok: false, suggestion: fixed.every(Boolean) ? fixed.join(" ") : null };
  });
}

// Which column of a pair list is English (for a language list only that side is checked).
export function englishSide(type) {
  return type === "language" ? "a" : "both";
}
